import { createStore, StoreApi } from "zustand/vanilla";
import { useStore } from "zustand";
import {
  ExploreFilterSchemaFields,
  ExploreFilterSchemaType,
} from "@modules/new-explore/types";
import { ExplorePagination } from "types/api/new-explore";
import { ExploreCategory } from "@modules/layout/modal/explore/category";

export type ActiveFilterType = Record<
  ExploreFilterSchemaFields,
  {
    filter_label: string;
    filter_value: { label: string; id: string; parentId?: number }[];
  }
>;

export type ExploreState = {
  activeFilters: ActiveFilterType | undefined;
  paginationData: ExplorePagination | undefined;
  filtersData: ExploreFilterSchemaType | undefined;
  categoryData: ExploreCategory[] | undefined;
  count: number;
};

export type ExploreActions = {
  setActiveFilters: (filters: ActiveFilterType | undefined) => void;
  setPaginationData: (data: ExplorePagination | undefined) => void;
  setFiltersData: (data: ExploreFilterSchemaType | undefined) => void;
  setCategoryData: (data: ExploreCategory[] | undefined) => void;
  setExploreError: (error: any) => void;
};

export type ExploreStore = ExploreState & {
  exploreError: any;
  actions: ExploreActions;
};

export const initialState: ExploreState = {
  activeFilters: undefined,
  paginationData: undefined,
  filtersData: undefined,
  categoryData: undefined,
  count: 24,
};

export const initExploreStore = (preloaded?: Partial<ExploreStore>) =>
  createStore<ExploreStore>((set) => ({
    ...initialState,
    ...preloaded,
    exploreError: preloaded?.exploreError ?? undefined,

    actions: {
      setActiveFilters: (activeFilters) => set({ activeFilters }),
      setPaginationData: (paginationData) => set({ paginationData }),
      setFiltersData: (filtersData) => set({ filtersData }),
      setCategoryData: (categoryData) => set({ categoryData }),
      setExploreError: (exploreError) => set({ exploreError }),
    },
  }));

let clientStore: StoreApi<ExploreStore> | null = null;

export function useExploreStore<T>(
  selector: (state: ExploreStore) => T,
  preloaded?: Partial<ExploreStore>
): T {
  const isServer = typeof window === "undefined";

  if (isServer) {
    const store = initExploreStore(preloaded);
    return useStore(store, selector);
  }

  if (!clientStore) {
    clientStore = initExploreStore(preloaded);
  }

  return useStore(clientStore, selector);
}
